import { brandAssets } from '@echo/brand';
import logo from '@echo/brand/assets/logo-lockup.png';
import { useAcceptInvite, useMyInvite } from '@echo/core/react';
import { Button } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { GroupActionFailed, GroupLoading } from '@/components/group-load-state';
import { GroupPrivacy } from '@/components/group-privacy';
import { InvitedGroupCard, InvitedGroupRow } from '@/components/invited-group-card';

export const Route = createFileRoute('/_app/invite/$inviteId')({ component: JoinGroupPage });

/**
 * Join group (Figma invite), opened from the link in an invitation (`/invite/{uuid}`): which
 * group, who invited them, what its leaders will see, then "Join group" or "Not now" (the
 * invitation stays open). Core only shows an invitation to the account it was sent to; one
 * already answered, withdrawn or expired says so instead. Someone not yet in has the link held
 * by the app layout and comes back here once they are.
 */
function JoinGroupPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { inviteId } = Route.useParams();
  const invite = useMyInvite(inviteId);
  const joining = useAcceptInvite();
  const home = () => void navigate({ to: '/' });
  const invitation = invite.data;

  if (invite.isPending) return <GroupLoading />;
  if (invite.isError) {
    return (
      <Notice
        message={t`This invitation couldn't be loaded. Check your connection and try again.`}
        action={t`Try again`}
        onAction={() => void invite.refetch()}
        onClose={home}
      />
    );
  }
  if (!invitation) {
    return (
      <Notice
        message={t`This invitation isn't for this account. Sign in with the email or phone number it was sent to.`}
        onClose={home}
      />
    );
  }
  // Joining answers the invitation: this page stays as it was until You're in! replaces it.
  if (invitation.status === 'accepted' && !joining.isSuccess) {
    const groupName = invitation.groupName;
    return (
      <Notice
        message={t`You're already in ${groupName}.`}
        action={t`Go to group`}
        onAction={() =>
          void navigate({ to: '/account/groups/$id', params: { id: invitation.groupActorUuid } })
        }
        onClose={home}
      />
    );
  }
  if (invitation.status === 'closed') {
    return (
      <Notice
        message={t`This invitation can no longer be used. Ask the group's leader to invite you again.`}
        onClose={home}
      />
    );
  }

  const join = () => {
    if (joining.isPending) return;
    joining.mutate(invitation.id, {
      onSuccess: () =>
        void navigate({
          to: '/invite/$inviteId/joined',
          params: { inviteId: invitation.id },
          replace: true,
        }),
    });
  };
  return (
    <div className="flex flex-col gap-section-sm">
      <img
        src={logo}
        alt="Stronger Memory"
        width={brandAssets.logoLockup.width}
        height={brandAssets.logoLockup.height}
        className="h-auto w-[180px] self-center"
      />
      <div className="flex flex-col gap-content-lg">
        <div className="flex flex-col text-center">
          <p className="text-body-md text-on-surface-medium">
            <Trans>You're invited to join a group</Trans>
          </p>
          <h1 className="text-headline-md font-bold">{invitation.groupName}</h1>
        </div>
        <InvitedGroupCard invitation={invitation}>
          {invitation.invitedBy ? (
            <InvitedGroupRow label={t`Invited by`} value={invitation.invitedBy} />
          ) : null}
        </InvitedGroupCard>
        <GroupPrivacy />
      </div>
      <div className="flex flex-col items-end gap-content-sm">
        {joining.isError ? <GroupActionFailed /> : null}
        <div className="flex flex-wrap justify-end gap-content-sm">
          <Button variant="ghost" onClick={home}>
            {t`Not now`}
          </Button>
          <Button loading={joining.isPending} onClick={join}>
            {t`Join group`}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** An invitation that can't be joined from here: why, and the way on. */
function Notice({
  message,
  action,
  onAction,
  onClose,
}: {
  message: string;
  action?: string;
  onAction?: () => void;
  onClose: () => void;
}) {
  const { t } = useLingui();
  return (
    <div className="flex flex-col gap-content-lg">
      <p className="text-body-lg text-on-surface-medium">{message}</p>
      <div className="flex flex-wrap justify-end gap-content-sm">
        <Button variant={action ? 'ghost' : 'primary'} onClick={onClose}>
          {t`Go to home`}
        </Button>
        {action && onAction ? <Button onClick={onAction}>{action}</Button> : null}
      </div>
    </div>
  );
}
